'use client'

import * as React from 'react'

import { cn } from '../../../lib/utils'
import Icon, { iconTypes } from '../Icon'
import s from '../../styles/animations.module.css'
import '../../styles/colorPicker.css'

import MetafieldLabel from '../MetafieldLabel'
import ColorPicker, { ColorPickerProps } from '../ColorPicker'
import { FieldChange } from '../FieldChange'

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  type: 'text' | 'number' | 'password' | 'email' | 'tel' | 'url' | 'multiline'
  format?: 'currency' | 'number'
  currency?: {
    code: string
    divider: number
  }
  inputSize?: 'default' | 'lg' | 'xl'
  inputWrapperClassName?: string
  outerWrapperClassName?: string
  labelClassName?: string
  debounceTime?: number
  leftIcon?: iconTypes
  rightIcon?: iconTypes
  isLoading?: boolean
  isSkeleton?: boolean
  isDisabled?: boolean
  isValidated?: boolean
  label?: string
  errorMessage?: string
  helpText?: string
  id: string
  textAlignment?: 'left' | 'center' | 'right'
  suffix?: string
  badge?: {
    label: string
    icon: iconTypes
    handleClick: () => void | Promise<void>
  }
  value?: string | number
  originalValue?: string | number | null
  handleChange?: (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => Promise<void> | void
  handleKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  handleValidation?: (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => Promise<void> | void
  colorPicker?: ColorPickerProps
  metafield?: {
    label: string
    href: string
  }
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      inputWrapperClassName,
      outerWrapperClassName,
      labelClassName,
      type,
      id,
      label,
      leftIcon,
      rightIcon,
      isLoading,
      isSkeleton,
      isValidated,
      inputSize = 'default',
      textAlignment,
      isDisabled,
      errorMessage,
      helpText,
      prefix,
      suffix,
      badge,
      value,
      originalValue,
      handleChange,
      handleKeyDown,
      handleValidation,
      colorPicker,
      max,
      min,
      format,
      currency,
      metafield,
      debounceTime = 500,
      ...props
    },
    ref,
  ) => {
    const formatter = currency
      ? new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currency?.code,
        })
      : null

    if (currency && format == 'currency') {
      value = value !== undefined ? Number(value) / currency.divider : undefined
    }

    const isAbsentValue = (
      val: string | number | null | undefined,
    ): boolean => {
      return val === null || val === undefined || val === ''
    }

    const [inputValue, setInputValue] = React.useState<string | number>(
      value !== undefined ? value : '',
    )
    const [lastChange, setLastChange] = React.useState<string | number>(
      value !== undefined ? value : '',
    )

    const [currencyVisible, setCurrencyVisible] = React.useState<boolean>(true)

    const setInputValueHandler = (nextValue: string | number) => {
      setInputValue(nextValue)
    }

    const hasChangedFromOutside = (nextValue: string | number | undefined) => {
      return (
        lastChange == '' ||
        (lastChange == inputValue &&
          inputValue != nextValue &&
          nextValue !== undefined)
      )
    }

    // first render
    React.useEffect(() => {
      // Treat numeric 0 as a present value (it is falsy in JS).
      if (isAbsentValue(inputValue) && !isAbsentValue(value)) {
        setLastChange(value as string | number)
        return setInputValueHandler(value as string | number)
      }

      // if value changes from outside, update input value
      if (hasChangedFromOutside(value) && value !== undefined) {
        setLastChange(value)
        return setInputValueHandler(value)
      }
    }, [value])

    React.useEffect(() => {
      const divisor = currency ? currency.divider : 1

      if (
        type == 'number' &&
        !isAbsentValue(value) &&
        max !== undefined &&
        Number(value) >= Number(max) / divisor
      ) {
        setLastChange(Number(max) / divisor)
        return setInputValueHandler(Number(max) / divisor)
      }

      if (
        type == 'number' &&
        !isAbsentValue(value) &&
        min !== undefined &&
        Number(value) <= Number(min) / divisor
      ) {
        setLastChange(Number(min) / divisor)
        return setInputValueHandler(Number(min) / divisor)
      }
    }, [max, min])

    // Helper function to normalize values for comparison
    const normalizeValue = (
      val: string | number | null | undefined,
    ): string => {
      if (val === null || val === undefined || val === '') {
        return '( None )'
      }
      return String(val)
    }

    // Check if we should show FieldChange component
    const shouldShowFieldChange =
      originalValue !== undefined &&
      normalizeValue(originalValue) !== normalizeValue(value)

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setCurrencyVisible(false)
    }

    const triggerChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (format == 'currency' && currency) {
          handleChange &&
            handleChange({
              ...e,
              target: {
                ...e.target,
                value: String(Number(e.target.value) * currency.divider),
              },
            })
        } else {
          handleChange && handleChange(e)
        }
      },
      [handleChange, format, currency],
    )

    const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
    const pendingChangeRef =
      React.useRef<React.ChangeEvent<HTMLInputElement> | null>(null)

    const flushPendingChange = React.useCallback(() => {
      const pending = pendingChangeRef.current
      if (!pending) return

      if (timerRef.current !== null) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }

      triggerChange(pending)
      setLastChange(pending.target.value)
      pendingChangeRef.current = null
    }, [triggerChange])

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setCurrencyVisible(true)
      flushPendingChange()
    }

    React.useEffect(() => {
      return () => {
        if (timerRef.current !== null) {
          clearTimeout(timerRef.current)
        }
      }
    }, [])

    // debounce function calling timeout
    const debounce = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        // run the function to indicate that the input has been triggered
        handleValidation && handleValidation(e)

        if (type == 'number' && e.target.value == '') {
          // Empty number fields resolve to min (or 0). Keep going so 0 registers.
          const fallback = min !== undefined && min !== null ? Number(min) : 0
          e.target.value = String(Number.isNaN(fallback) ? 0 : fallback)
        }

        if (type == 'number' && e.target.value != '0') {
          e.target.value = e.target.value.replace(/^0+/, '')
        }

        pendingChangeRef.current = e

        if (timerRef.current !== null) {
          clearTimeout(timerRef.current)
        }

        const divisor = currency ? currency.divider : 1

        // if the value is a number and above the max value, set it to the max value
        if (
          type == 'number' &&
          max !== undefined &&
          Number(e.target.value) > Number(max) / divisor
        ) {
          setInputValueHandler(Number(max) / divisor)
        }
        // if the value is a number and below the min value, set it to the min value
        else if (
          type == 'number' &&
          min !== undefined &&
          Number(e.target.value) < Number(min) / divisor
        ) {
          setInputValueHandler(Number(min) / divisor)
        } else {
          setInputValueHandler(e.target.value)
        }

        if (debounceTime === 0) {
          triggerChange(e)
          setLastChange(e.target.value)
          pendingChangeRef.current = null
          return
        }

        timerRef.current = setTimeout(() => {
          triggerChange(e)
          setLastChange(e.target.value)
          pendingChangeRef.current = null
          timerRef.current = null
        }, debounceTime)
      },
      [currency, debounceTime, handleValidation, max, min, triggerChange, type],
    )

    if (isSkeleton) {
      return (
        <div className="w-full">
          <label
            className={cn({
              'text-destructive opacity-100 leading-tight w-full': errorMessage,
            })}
          >
            {label}
            <div className="h-9 w-full bg-secondary animate-pulse rounded-md border border-input text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-within:outline-none focus-within:ring-1 focus-within:ring-ring disabled:cursor-not-allowed disabled:opacity-50"></div>
          </label>
        </div>
      )
    }

    return (
      <div className={cn('w-full bg-transparent', outerWrapperClassName)}>
        <label
          className={cn({
            'text-destructive opacity-100 leading-tight w-full': errorMessage,
          })}
        >
          {label && (
            <div
              className={cn(
                'flex justify-between items-center text-muted-foreground mb-1 h-5',
                className,
                labelClassName,
              )}
            >
              {label}
              <div className="flex items-center gap-2">
                {shouldShowFieldChange && (
                  <FieldChange
                    oldValue={normalizeValue(originalValue)}
                    newValue={normalizeValue(value)}
                    size="sm"
                    handleRevert={() => {
                      if (handleChange) {
                        const syntheticEvent = {
                          target: {
                            value:
                              originalValue === null ||
                              originalValue === undefined
                                ? ''
                                : String(originalValue),
                          },
                        } as React.ChangeEvent<HTMLInputElement>
                        handleChange(syntheticEvent)
                      }
                    }}
                  />
                )}
                {metafield && <MetafieldLabel {...metafield} />}
              </div>
            </div>
          )}
          <div
            title={`input-wrapper-${id}`}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onClick={() => setCurrencyVisible(false)}
            className={cn(
              'text-primary stroke-current cursor-text flex items-center justify-between gap-3 h-9 w-full rounded-md border border-input focus-within:border-primary bg-transparent px-3 text-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50',
              inputWrapperClassName,
              className,
              {
                'h-10': inputSize == 'lg',
                'h-12': inputSize == 'xl',
              },
            )}
          >
            {leftIcon && !isLoading && isValidated && (
              <Icon
                name="Check"
                height={15}
                width={15}
                className="opacity-50"
              />
            )}
            {leftIcon && isLoading && (
              <Icon
                name="LoaderCircle"
                height={15}
                width={15}
                className="animate-spin opacity-50"
              />
            )}
            {leftIcon && !isLoading && !isValidated && (
              <Icon
                name={leftIcon}
                height={15}
                width={15}
                className="opacity-50"
              />
            )}
            <div className="flex flex-1 items-center gap-1">
              {prefix && !isAbsentValue(value) && (
                <div className="text-muted-foreground flex items-center leading-none">
                  {prefix}
                </div>
              )}

              <input
                id={id}
                type={type}
                autoComplete={type == 'number' ? 'off' : undefined}
                disabled={isDisabled}
                title={`input-${id}`}
                className={cn(
                  'text-primary w-full flex-0 flexrounded-md h-8 bg-transparent text-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none  disabled:cursor-not-allowed disabled:opacity-50',
                  className,
                  {
                    'text-left': textAlignment === 'left',
                    'text-center': textAlignment === 'center',
                    'text-right': textAlignment === 'right',
                    'h-10': inputSize == 'lg',
                    'h-12': inputSize == 'xl',
                    hidden: format == 'currency' && currencyVisible,
                    flex: format == 'currency' && !currencyVisible,
                  },
                )}
                max={max}
                min={min}
                ref={ref}
                // for some reason we need to adjust this to be pixel perfect
                style={{ marginTop: -1 }}
                {...props}
                value={inputValue}
                onChange={debounce}
                onKeyDown={handleKeyDown}
              />
              {format == 'currency' && currencyVisible && (
                <div
                  title={`input-currency-${id}`}
                  onClick={() => setCurrencyVisible(false)}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  className={cn(
                    'leading-none items-center whitespace-nowrap w-full',
                    {
                      'text-right': textAlignment == 'right',
                      'text-left': textAlignment == 'left',
                    },
                  )}
                >
                  {inputValue !== undefined &&
                    formatter &&
                    formatter.format(Number(inputValue))}
                </div>
              )}
              {badge && (
                <button
                  title={`${props.title}-suffix`}
                  onClick={() => badge.handleClick()}
                  className="border text-muted-foreground  h-full text-nowrap py-1 px-2 rounded text-2xs flex items-center justify-center gap-1 leading-none hover:border-primary hover:text-primary"
                >
                  {badge.icon && <Icon size={10} name={badge.icon} />}
                  {badge.label}
                </button>
              )}

              {suffix && (
                <div
                  title={`${props.title}-suffix`}
                  className="text-muted-foreground leading-none items-center whitespace-nowrap h-full"
                >
                  {suffix}
                </div>
              )}
            </div>
            {colorPicker && (
              <div className="w-6 flex items-center justify-center h-full">
                {colorPicker && <ColorPicker {...colorPicker} />}
              </div>
            )}
            {rightIcon && (
              <Icon
                name={rightIcon}
                height={15}
                width={15}
                className="opacity-50"
              />
            )}
          </div>
        </label>
        {helpText && (
          <div className="text-muted-foreground normal-case text-xs mt-2">
            {helpText}
          </div>
        )}
        {errorMessage && (
          <div
            title={`input-error-${id}`}
            className={cn('text-destructive text-xs mt-2', s.smallTransitionIn)}
          >
            {errorMessage}
          </div>
        )}
      </div>
    )
  },
)
Input.displayName = 'Input'

export { Input }
